<script setup>
import { computed, h } from 'vue';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
} from '@tanstack/vue-table';
import { useI18n } from 'vue-i18n';

import Spinner from 'shared/components/Spinner.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import Table from 'dashboard/components/table/Table.vue';
import Pagination from 'dashboard/components/table/PaginationDetail.vue';
import AgentCell from './AgentCell.vue';

const { agents, agentMetrics, pageIndex, total, pageSize } = defineProps({
  agents: {
    type: Array,
    default: () => [],
  },
  agentMetrics: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  pageIndex: {
    type: Number,
    default: 1,
  },
  total: {
    type: Number,
    default: 0,
  },
  pageSize: {
    type: Number,
    default: 9,
  },
});

const emit = defineEmits(['pageChange']);
const { t } = useI18n();

function getAgentInformation(id) {
  return agents?.find(agent => agent.id === Number(id));
}

const totalCount = computed(() => agents.length);
const tableData = computed(() => {
  return agentMetrics
    .filter(agentMetric => getAgentInformation(agentMetric.id))
    .map(agent => {
      const agentInformation = getAgentInformation(agent.id);
      return {
        id: agentInformation.id,
        agent: agentInformation.name || agentInformation.available_name,
        email: agentInformation.email,
        thumbnail: agentInformation.thumbnail,
        open: agent.metric.open ?? 0,
        unattended: agent.metric.unattended ?? 0,
        status: agentInformation.availability_status,
      };
    });
});

const defaulSpanRender = cellProps =>
  h(
    'span',
    {
      class: cellProps.getValue() ? '' : 'text-slate-300 dark:text-slate-700',
    },
    cellProps.getValue() ? cellProps.getValue() : '---'
  );

  const statusCell = cellProps => {
  const value = (cellProps.getValue() || '').toLowerCase();
  const map = {
    online: {
      text: 'Online',
      cls: 'text-green-500 bg-[rgba(18,183,106,0.05)]',
    },
    offline: {
      text: 'Offline',
      cls: 'text-gray-500 bg-n-gray-1 dark:bg-[#FAFAFA0D]',
    },
  };
  const conf = map[value] || { text: value || '—', cls: 'text-gray-400 bg-transparent' };

  return h(
    'span',
    { class: `inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${conf.cls}` },
    conf.text
  );
};
const columnHelper = createColumnHelper();
const columns = [
  columnHelper.accessor('agent', {
    header: t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.TABLE_HEADER.AGENT'),
    cell: cellProps => h(AgentCell, cellProps),
  }),
  columnHelper.accessor('open', {
    header: t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.TABLE_HEADER.OPEN'),
    cell: defaulSpanRender,
  }),
  columnHelper.accessor('status', {
    header: t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.TABLE_HEADER.STATUS'),
    cell: statusCell,
  }),
  columnHelper.accessor('unattended', {
    header: () => t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.TABLE_HEADER.UNATTENDED'),
    cell: cellProps => h(
      'span',
      {
        class: `ml-2 ${cellProps.getValue() ? '' : 'text-slate-300 dark:text-slate-700'}`,
      },
      cellProps.getValue() ? cellProps.getValue() : '---'
    ),
    size: 154,
  }),
];

const paginationParams = computed(() => ({
  pageIndex: pageIndex,
  pageSize: pageSize,
}));

const table = useVueTable({
  get data() {
    return tableData.value;
  },
  columns,
  manualPagination: true,
  enableSorting: false,
  getCoreRowModel: getCoreRowModel(),
  get rowCount() {
    // Prefer server-provided total when available; fallback to client length
    return total || totalCount.value;
  },
  state: {
    get pagination() {
      return paginationParams.value;
    },
  },
  onPaginationChange: updater => {
    const newPagintaion = updater(paginationParams.value);
    emit('pageChange', newPagintaion.pageIndex);
  },
});
</script>

<template>
  <div class="rounded-xl bg-n-solid-2 p-6 shadow-sm border border-n-weak">
    <div class="agent-table-container">
      <Table :table="table" :borderless="true" :minimal="true" class="max-h-[calc(100vh-21.875rem)]" />
      <Pagination class="mt-4" :table="table" />
      <div v-if="isLoading" class="agents-loader">
        <Spinner />
        <span>{{
          $t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.LOADING_MESSAGE')
          }}</span>
      </div>
      <EmptyState v-else-if="!isLoading && !agentMetrics.length"
        :title="$t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.NO_AGENTS')" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.agent-table-container {
  @apply flex flex-col flex-1;

  .ve-table {
    &::v-deep {
      th.ve-table-header-th {
        @apply text-sm rounded-xl;
        padding: var(--space-small) var(--space-two) !important;
      }

      td.ve-table-body-td {
        padding: var(--space-one) var(--space-two) !important;
      }

      .ve-table-body .ve-table-body-tr:last-child td {
        @apply border-b-0;
      }
    }
  }

  &::v-deep .ve-pagination {
    @apply bg-transparent dark:bg-transparent;
  }

  &::v-deep .ve-pagination-select {
    @apply hidden;
  }

  .row-user-block {
    @apply items-center flex text-left;

    .user-block {
      @apply items-start flex flex-col min-w-0 my-0 mx-2;

      .title {
        @apply text-sm m-0 leading-[1.2] text-slate-800 dark:text-slate-100;
      }

      .sub-title {
        @apply text-xs text-slate-600 dark:text-slate-200;
      }
    }
  }

  .table-pagination {
    @apply mt-4 text-right;
  }
}

.agents-loader {
  @apply items-center flex text-base justify-center p-8;
}
</style>
